import { Breadcrumb } from "@gull";
import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Button,
  Col,
  Container,
  Form as BootstrapForm,
  Row,
  Spinner,
} from "react-bootstrap";
import {
  bankDetailsFetch,
  bankDetailsUpload,
} from "app/services/bannerSettingService";
import { NotificationManager } from "react-notifications";
import BankDetailsModal from "./Components/BankDetailsModal";

const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
const maxFileSize = 4 * 1024 * 1024; // 4MB in bytes

const bankDetailsSchema = Yup.object().shape({
  AccountHolderName: Yup.string().required("Account Holder Name is required"),
  BankName: Yup.string().required("Bank Name is required"),
  AccountNumber: Yup.string()
    .matches(/^\d+$/, "Account Number must be numeric")
    .required("Account Number is required"),
  IFSCCode: Yup.string()
    // .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Invalid IFSC Code format")
    .required("IFSC Code is required"),
  BranchName: Yup.string().required("Branch Name is required"),
  BankSortName: Yup.string().optional(),
  QRImage: Yup.mixed()
    .test("fileType", "Only PNG, JPG, and JPEG files are allowed", (value) => {
      if (!value) return true; // Allow empty field
      return allowedTypes.includes(value.type);
    })
    .test("fileSize", "File size should be less than 4MB", (value) => {
      if (!value) return true; // Allow empty field
      return value.size <= maxFileSize;
    }),
});

export default function BankDetailsUpload() {
  const initialValues = {
    AccountHolderName: "",
    BankName: "",
    AccountNumber: "",
    IFSCCode: "",
    BranchName: "",
    BankSortName: "",
    QRImage: undefined,
  };
  const [show, setShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);

  const handleClose = () => {
    setShow(false);
  };

  const handleShowBankDetails = async () => {
    setIsLoading(true);
    setTimeout(async () => {
      try {
        const fetchRes = await bankDetailsFetch();
        setBankDetails(fetchRes?.data); // Store the fetched data

        // Open modal after data is fetched
        setShow(true);
      } catch (error) {
        console.error("Error fetching bank details:", error);
      }
      setIsLoading(false);
    }, 1000);
  };
  const handleSubmit = async (values, { resetForm }) => {
    debugger;
    setIsLoading(true);
    const formData = new FormData();
    formData.append("AccountHolderName", values.AccountHolderName);
    formData.append("BankName", values.BankName);
    formData.append("AccountNumber", values.AccountNumber);
    formData.append("IFSCCode", values.IFSCCode);
    formData.append("BranchName", values.BranchName);
    formData.append("BankSortName", values.BankSortName);
    if (values.QRImage) {
      formData.append("QRImage", values.QRImage);
    }

    try {
      const response = await bankDetailsUpload(formData);
      if (response?.status == "Success") {
        NotificationManager.success(response?.message);
        // console.log("Response:", response);
        resetForm();
        setTimeout(() => {
          handleShowBankDetails();
        }, 500);
      } else {
        NotificationManager.error(response?.message);
        // console.log("Response:", response);
      }
    } catch (error) {
      NotificationManager.error("Error uploading bank details");
      //   console.error("Error:", error);
    }
    setIsLoading(false);
  };

  return (
    <Container>
      <div>
        <BankDetailsModal
          handleShowBankDetails={handleShowBankDetails}
          handleClose={handleClose}
          show={show}
          isLoading={isLoading}
          bankDetails={bankDetails}
        />
      </div>

      <Helmet>
        <title>Bank Details Upload</title>
      </Helmet>
      <Breadcrumb routeSegments={[{ name: "Bank Details Upload" }]} />

      <Formik
        initialValues={initialValues}
        validationSchema={bankDetailsSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, resetForm }) => (
          <BootstrapForm as={Form} className="p-4 border rounded shadow">
            <Row className="mb-3">
              <Col md={6}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label>Account Holder Name</BootstrapForm.Label>
                  <Field
                    name="AccountHolderName"
                    type="text"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="AccountHolderName"
                    component="div"
                    className="text-danger"
                  />
                </BootstrapForm.Group>
              </Col>
              <Col md={6}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label>Bank Name</BootstrapForm.Label>
                  <Field name="BankName" type="text" className="form-control" />
                  <ErrorMessage
                    name="BankName"
                    component="div"
                    className="text-danger"
                  />
                </BootstrapForm.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label>Account Number</BootstrapForm.Label>
                  <Field
                    name="AccountNumber"
                    type="text"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="AccountNumber"
                    component="div"
                    className="text-danger"
                  />
                </BootstrapForm.Group>
              </Col>
              <Col md={6}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label>IFSC Code</BootstrapForm.Label>
                  <Field name="IFSCCode" type="text" className="form-control" />
                  <ErrorMessage
                    name="IFSCCode"
                    component="div"
                    className="text-danger"
                  />
                </BootstrapForm.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label>Branch Name</BootstrapForm.Label>
                  <Field
                    name="BranchName"
                    type="text"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="BranchName"
                    component="div"
                    className="text-danger"
                  />
                </BootstrapForm.Group>
              </Col>
              <Col md={6}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label>Bank Sort Name</BootstrapForm.Label>
                  <Field
                    name="BankSortName"
                    type="text"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="BankSortName"
                    component="div"
                    className="text-danger"
                  />
                </BootstrapForm.Group>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={12}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label>QR Image</BootstrapForm.Label>
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    className="form-control"
                    onChange={(event) =>
                      setFieldValue("QRImage", event.currentTarget.files[0])
                    }
                  />
                  <ErrorMessage
                    name="QRImage"
                    component="div"
                    className="text-danger"
                  />
                </BootstrapForm.Group>
              </Col>
            </Row>

            <div className="text-center">
              {isLoading ? (
                <Button type="submit" variant="primary" disabled>
                  <Spinner size="sm" animation="border" className="me-1" />
                  Please wait...
                </Button>
              ) : (
                <Button type="submit" variant="primary">
                  Submit
                </Button>
              )}
            </div>
          </BootstrapForm>
        )}
      </Formik>
    </Container>
  );
}
